import { useQuery } from "@apollo/client";
import { useState, useEffect } from "react";
import {
  GET_PIE_CHART_SPENDING,
  GET_TOP_SPENDING,
} from "../graphql/transaction";
import { TimeFrameEnum } from "../enums/AnalyticsTimeFrame";
import type { AnalyticsFilters } from "components/admin/analytics/analyticsFilters";

type SpendingType = "pie" | "bar";

interface CategoryAmount {
  categoryName: string;
  amount: number;
}

interface SpendingItem {
  name: string;
  monthKey: string;
  categoryAmounts: CategoryAmount[];
}

export interface SpendingMonth {
  name: string;
  monthKey: string;
  [categoryItem: string]: number | string;
}

export interface PieItem {
  name: string;
  value: number;
}

interface UseSpendingDataOptions {
  type: SpendingType;
  groupKey: string;
  filters?: Partial<AnalyticsFilters>;
}

const useSpendingData = ({
  type,
  groupKey,
  filters,
}: UseSpendingDataOptions) => {
  const [currency, setCurrency] = useState<string>("");
  const [pieData, setPieData] = useState<PieItem[]>([]);
  const [barData, setBarData] = useState<{
    data: SpendingMonth[];
    keys: string[];
  }>({
    data: [],
    keys: [],
  });

  const timeFrame = filters?.timeFrame || TimeFrameEnum.LAST_90_DAYS;

  const queryVariables = {
    groupKey,
    dateDescriptor: timeFrame,
    ...(filters?.accountId && { accountId: filters.accountId }),
    ...(filters?.employee?.id && { userId: filters.employee.id }),
    ...(filters?.card?.id && { cardId: filters.card.id }),
    ...(filters?.expenseCategory?.id && {
      expenseCategoryId: filters.expenseCategory.id,
    }),
    ...(filters?.allocationType && {
      allocationType: filters.allocationType,
    }),
    ...(filters?.allocation?.id && { allocationId: filters.allocation.id }),
  };

  const { data, loading, refetch } = useQuery(
    type === "pie" ? GET_PIE_CHART_SPENDING : GET_TOP_SPENDING,
    {
      variables: queryVariables,
      fetchPolicy: "cache-and-network",
    },
  );

  // New variables with nothing cached leave `data` undefined while they load.
  // Drop the previous answer then, so it is never shown as this one's.
  useEffect(() => {
    if (data || !loading) return;
    setPieData((current) => (current.length === 0 ? current : []));
    setBarData((current) =>
      current.data.length === 0 && current.keys.length === 0
        ? current
        : { data: [], keys: [] },
    );
  }, [data, loading]);

  useEffect(() => {
    if (!data) return;

    if (type === "pie") {
      const pieChartData = data.pieChartSpending;
      setCurrency(pieChartData?.currency || "");
      setPieData(pieChartData?.data || []);
    } else {
      const topSpendingData = data.topSpending;
      setCurrency(topSpendingData?.currency || "");

      const keys = topSpendingData?.keys || [];
      const formattedData =
        topSpendingData?.data?.map((item: SpendingItem) => {
          const reformattedItem: SpendingMonth = {
            name: item.name,
            monthKey: item.monthKey,
          };

          item.categoryAmounts.forEach((categoryAmount: CategoryAmount) => {
            reformattedItem[categoryAmount.categoryName] =
              categoryAmount.amount;
          });

          return reformattedItem;
        }) || [];

      setBarData({ data: formattedData, keys });
    }
  }, [data, type]);

  return {
    loading,
    currency,
    pieData,
    barData,
    refetch,
  };
};

export default useSpendingData;
